/**
 * Resolver HÍBRIDO del karaoke (Escuchar). Combina el glosario local pre-fetcheado
 * (cero red, instantáneo) con re-resolución contextual contra `/lookup` (LLM con
 * la ORACIÓN) cuando hace falta — el mismo `/lookup` que usa la conversación:
 *
 *   1. Palabra MONOSÉMICA  → glosario local (gratis, sin red).
 *   2. Palabra POLISÉMICA  → `/lookup` con la oración como contexto (LLM). El
 *      diccionario suelto da el sentido global, que en esa frase puede estar mal;
 *      con contexto se desambigua. Conservamos ipa/cefr/senses del léxico.
 *   3. FRASE                → `/lookup` con la oración como contexto (LLM).
 *
 * Señal de polisemia: `senses.length > 1` (lo trae el `lexicon_entries`). Las
 * palabras que sólo existen en el diccionario global llegan con `senses: []` →
 * se tratan como monosémicas (límite conocido: idealmente el build del glosario
 * debería marcar la ambigüedad; ver 19.F).
 */

import { peekLookup, resolveLookup } from './conversationResolver.js';
import { normalizeWord, type WordLookupEntry, type WordResolver } from './types.js';

function isSingleWord(text: string): boolean {
  return !/\s/.test(text.trim());
}

function isPolysemic(e: WordLookupEntry | undefined): boolean {
  return !!e && Array.isArray(e.senses) && e.senses.length > 1;
}

/** Mergea los campos ricos del léxico (ipa/cefr/senses) sobre un resultado del
 *  `/lookup` (que no los trae), para que el popover muestre lo mismo que la
 *  Biblioteca aunque la traducción venga re-resuelta con contexto. */
function withLexiconRich(r: WordLookupEntry, e: WordLookupEntry | undefined): WordLookupEntry {
  if (!e) return r;
  // `exactOptionalPropertyTypes`: sólo sumamos los campos ricos que estén
  // definidos (no asignamos `undefined` a una prop opcional).
  return {
    ...r,
    ...(e.ipa !== undefined ? { ipa: e.ipa } : {}),
    ...(e.cefr !== undefined ? { cefr: e.cefr } : {}),
    ...(e.senses !== undefined ? { senses: e.senses } : {}),
  };
}

export function makeListenGlossaryResolver(
  glossary: Record<string, WordLookupEntry> | null,
): WordResolver {
  const localOf = (text: string): WordLookupEntry | undefined =>
    glossary ? glossary[normalizeWord(text)] : undefined;

  return {
    peekWord: (text, context): WordLookupEntry | undefined => {
      if (isSingleWord(text)) {
        const e = localOf(text);
        // Monosémica → instantánea. Polisémica/ausente → que vaya a resolveWord
        // (contexto); igual devolvemos un cache-hit con contexto si ya existe.
        if (e && !isPolysemic(e)) return e;
        return peekLookup(text, context);
      }
      return peekLookup(text, context); // frase
    },
    // Polisémica sin cache: mostramos YA la entrada del léxico (traducción
    // global + ipa/cefr/acepciones) y dejamos que `resolveWord` refine la
    // traducción con la oración. Sin esto el tap era ~2 s de spinner teniendo
    // el 90% del contenido a mano.
    peekProvisional: (text): WordLookupEntry | undefined =>
      isSingleWord(text) ? localOf(text) : undefined,
    resolveWord: async (text, context): Promise<WordLookupEntry> => {
      if (isSingleWord(text)) {
        const e = localOf(text);
        if (e && !isPolysemic(e)) return e; // monosémica → glosario, sin red
        const r = await resolveLookup(text, context); // LLM con la oración
        return withLexiconRich(r, e);
      }
      return resolveLookup(text, context); // frase → LLM con la oración
    },
  };
}
